import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import { getWhatsAppConsent, setWhatsAppConsent } from "../../whatsappApi";
import { whatsAppFeatureEnabled } from "./access";

/**
 * PatientWhatsAppConsent — consent STATUS for a saved patient, with inline
 * capture.
 *
 * For surfaces that act on a patient but do not own the patient record — the
 * appointment modals, chiefly. Consent is a property of the patient, not of the
 * appointment, so this reads and writes the patient record rather than copying
 * the flag onto the appointment where the two could then disagree.
 *
 * It renders nothing at all when the practice does not have the WhatsApp
 * feature, so the appointment modals are unchanged for everyone else.
 *
 * Differs from WhatsAppConsentToggle, which is for a form that already holds
 * the patient (and its unsaved draft) in state and passes consent in.
 */
export default function PatientWhatsAppConsent({ patientId, className = "" }) {
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Feature/tier, not inbox access — see access.js.
  const enabled = whatsAppFeatureEnabled();

  useEffect(() => {
    if (!enabled || !patientId) {
      setConsent(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getWhatsAppConsent(patientId)
      .then((data) => {
        if (!cancelled) setConsent(data?.whatsappConsent || null);
      })
      .catch(() => {
        // Consent status is context, not the point of these screens — a failed
        // read must not break booking an appointment.
        if (!cancelled) setConsent(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, patientId]);

  if (!enabled || !patientId) return null;

  const grant = async () => {
    setSaving(true);
    try {
      const data = await setWhatsAppConsent(patientId, true, "practice");
      setConsent({ ...(data?.whatsappConsent || {}), granted: true });
    } catch (_err) {
      // Leave the prompt in place rather than claiming a consent the server
      // does not hold.
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`d-flex align-items-center gap-2 ${className}`}>
        <Spinner size="sm" animation="border" variant="primary" />
        <span className="small text-muted">Checking WhatsApp consent…</span>
      </div>
    );
  }

  if (consent?.granted) {
    const on = consent.grantedAt
      ? new Date(consent.grantedAt).toLocaleDateString("en-GB")
      : null;
    return (
      <div className={`d-flex align-items-center gap-2 ${className}`}>
        <i className="fa-brands fa-whatsapp text-success" />
        <span className="small">
          WhatsApp reminders on{on ? `: consent recorded ${on}` : ""}
        </span>
      </div>
    );
  }

  // Withdrawn is deliberately NOT re-grantable from here: re-opting in after a
  // patient has said STOP belongs on the patient record, where it is attributed.
  if (consent?.decided) {
    return (
      <div className={`d-flex align-items-center gap-2 ${className}`}>
        <i className="fa-brands fa-whatsapp text-danger" />
        <span className="small">
          This patient has opted out of WhatsApp. Re-enable it on the patient
          record.
        </span>
      </div>
    );
  }

  return (
    <div className={`d-flex align-items-center gap-2 ${className}`}>
      <i className="fa-brands fa-whatsapp text-muted" />
      <span className="small">No WhatsApp consent recorded.</span>
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            Record that the patient has agreed to WhatsApp messages about their
            appointments and account.
          </Tooltip>
        }
      >
        <Button
          variant="outline-primary"
          size="sm"
          onClick={grant}
          disabled={saving}
        >
          {saving ? (
            <Spinner size="sm" animation="border" />
          ) : (
            "Record consent"
          )}
        </Button>
      </OverlayTrigger>
    </div>
  );
}
